import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Layout, Flex, Input, Avatar, Badge, Typography, Button, Tooltip, Progress } from 'antd';
import { RobotOutlined, HistoryOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import ChannelSelect from '../ChannelSelect/ChannelSelect';
import { useTheme } from '../../../Shared/GlobalContext/ThemeContext';
import { getHistoricalImports } from '../../../Channels/ChannelsServices';
import { Resource, Action } from '../../../../Constants/socket';
import useSocketEvent from '../../../../Hooks/useSocketEvent';
import useSocketSubscription from '../../../../Hooks/useSocketSubscription';
import useSocket from '../../../../Hooks/useSocket';
import SharedFilterBar from '../../../Shared/Filters/SharedFilterBar';
import { conversationFilterFields } from '../../../Shared/Filters/filterConfig';
import useFilterOptionFields, { FILTER_OPTION_RESOURCE } from '../../../Shared/Filters/useFilterOptionFields';
import HistoricalImportModal from '../../../Channels/ChannelDetail/HistoricalImportsTab/HistoricalImportModal';
import './ConversationsHeader.css';

const { Header } = Layout;

const PROCESSING_STATUSES = ['pending', 'in_progress'];

const FILTERED_CONVERSATION_FILTER_FIELDS = conversationFilterFields.filter(
    field => !['channel_id', 'agent_id', 'contact_id'].includes(field.key)
);

const CONVERSATION_FILTER_OPTION_RESOURCES = {
};

const getImportStatusKey = (status) => {
    if (typeof status === 'string') return status;
    if (status && typeof status === 'object' && status.message_code) {
        const parts = status.message_code.split('.');
        return parts[parts.length - 1];
    }
    return '';
};

const ConversationsHeader = ({
    channels,
    loadingChannels,
    selectedChannel,
    onChannelChange,
    onChannelSearch,
    onSearch,
    loading,
    filters,
    onFiltersChange,
    sortValue,
    onSortChange,
}) => {
    const { isDarkMode } = useTheme();
    const { t } = useTranslation();
    const { socket } = useSocket();
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [activeImport, setActiveImport] = useState(null);
    const filterFields = useFilterOptionFields(FILTERED_CONVERSATION_FILTER_FIELDS, CONVERSATION_FILTER_OPTION_RESOURCES);

    const themeColors = {
        background: isDarkMode ? '#1f1f1f' : '#fff',
        border: isDarkMode ? '#434343' : '#f0f0f0',
        text: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.88)',
        secondaryText: isDarkMode ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
    };

    const currentChannel = channels?.find(c => c.id === selectedChannel);
    const agent = currentChannel?.agent;
    // Virtual channels don't support historical imports
    const canImport = currentChannel && !currentChannel.is_virtual;

    const handleOpenImportModal = useCallback(() => {
        setIsImportModalOpen(true);
    }, []);

    const handleCloseImportModal = useCallback(() => {
        setIsImportModalOpen(false);
    }, []);

    const fetchActiveImport = useCallback(async () => {
        if (!selectedChannel || !canImport) {
            setActiveImport(null);
            return;
        }
        try {
            const response = await getHistoricalImports(selectedChannel, {
                page: 1,
                size: 5,
                sort_by: 'created_at',
                sort_direction: 'desc',
                filters: { status: PROCESSING_STATUSES }
            });
            const latest = response?.data?.[0];
            setActiveImport(latest || null);
        } catch (error) {
            // Silently fail so it doesn't interrupt standard UX
        }
    }, [selectedChannel, canImport]);

    // Fetch active import on mount or channel change
    useEffect(() => {
        fetchActiveImport();
    }, [fetchActiveImport]);

    // Socket subscriptions for the active import
    const importIds = useMemo(() => {
        return activeImport?.id ? [String(activeImport.id)] : [];
    }, [activeImport]);
    useSocketSubscription(Resource.CHANNEL_HISTORICAL_IMPORT, importIds);

    const handleImportUpdate = useCallback((updatedImport) => {
        if (activeImport && updatedImport.id === activeImport.id) {
            const statusKey = getImportStatusKey(updatedImport.status || activeImport.status);
            if (PROCESSING_STATUSES.includes(statusKey)) {
                setActiveImport(prev => ({ ...prev, ...updatedImport }));
            } else {
                setActiveImport(null);
            }
        }
    }, [activeImport]);

    const handleImportCreate = useCallback((data) => {
        fetchActiveImport();

        // Subscribe to new import from its creation time to cover the readiness gap
        if (data?.id && data?.created_at && socket) {
            socket.subscribeToResource(Resource.CHANNEL_HISTORICAL_IMPORT, [data.id], data.created_at);
        }
    }, [fetchActiveImport, socket]);

    useSocketEvent(Resource.CHANNEL_HISTORICAL_IMPORT, Action.UPDATE, handleImportUpdate);
    useSocketEvent(Resource.CHANNEL_HISTORICAL_IMPORT, Action.CREATE, handleImportCreate);

    const handleImportSuccess = useCallback((newImport) => {
        const statusKey = newImport?.id ? getImportStatusKey(newImport.status) : '';
        if (PROCESSING_STATUSES.includes(statusKey)) {
            setActiveImport(newImport);
        } else {
            fetchActiveImport();
        }
    }, [fetchActiveImport]);


    return (
        <Header style={{
            padding: '8px',
            height: 'auto',
            lineHeight: 'normal',
            background: themeColors.background,
            borderBottom: `1px solid ${themeColors.border}`,
            transition: 'background 0.3s ease, border-color 0.3s ease'
        }}>
            {/* Channel Select */}
            <ChannelSelect
                channels={channels}
                loading={loadingChannels}
                value={selectedChannel}
                onChange={onChannelChange}
                onSearch={onChannelSearch}
            />

            {selectedChannel && (
                <div style={{
                    marginTop: 8,
                    marginBottom: 8,
                    padding: '8px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    alignContent: 'center',
                    flexWrap: 'wrap',
                    gap: 12,
                    background: isDarkMode
                        ? 'linear-gradient(145deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.02) 100%)'
                        : 'linear-gradient(145deg, #ffffff 0%, #f7f8fa 100%)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    boxShadow: isDarkMode
                        ? '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)'
                        : '0 2px 8px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.8)',
                    borderRadius: 10,
                    border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)'}`,
                    textAlign: 'center'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, flex: '0 1 auto', minWidth: 0 }}>
                        <div style={{ flexShrink: 0 }}>
                            {agent ? (
                                <Badge dot status={agent.is_started ? 'success' : 'default'} offset={[-2, 20]}>
                                    <Avatar
                                        size={28}
                                        icon={<RobotOutlined />}
                                        style={{
                                            backgroundColor: agent.is_started
                                                ? (isDarkMode ? 'rgba(82, 196, 26, 0.15)' : '#f6ffed')
                                                : (isDarkMode ? 'rgba(24, 144, 255, 0.15)' : '#e6f7ff'),
                                            color: agent.is_started
                                                ? '#52c41a'
                                                : (isDarkMode ? '#177ddc' : '#1890ff'),
                                            border: `1px solid ${agent.is_started ? 'rgba(82, 196, 26, 0.3)' : 'rgba(24, 144, 255, 0.3)'}`
                                        }}
                                    />
                                </Badge>
                            ) : (
                                <Avatar
                                    size={28}
                                    icon={<RobotOutlined />}
                                    style={{
                                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : '#f0f2f5',
                                        color: isDarkMode ? 'rgba(255, 255, 255, 0.35)' : '#bfbfbf',
                                        border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0,0,0,0.05)'}`
                                    }}
                                />
                            )}
                        </div>

                        <div style={{
                            fontSize: 13,
                            lineHeight: 1.3,
                            color: agent ? themeColors.text : themeColors.secondaryText,
                            fontWeight: agent ? 600 : 500,
                            letterSpacing: '-0.01em',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                            flex: '0 1 auto'
                        }}>
                            {agent ? agent.name : t('no_agent_yet')}
                        </div>
                    </div>

                    {/* Historical Import Button */}
                    {canImport && (
                        <div style={{ flexShrink: 0 }}>
                            {activeImport ? (
                                <Tooltip title={`${t('sync')} — ${Math.round(activeImport.progress || 0)}%`}>
                                    <Button
                                        type="primary"
                                        ghost
                                        size="small"
                                        onClick={handleOpenImportModal}
                                        style={{
                                            borderRadius: 100,
                                            fontSize: 12,
                                            fontWeight: 500,
                                            height: 28,
                                            padding: '0 12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            whiteSpace: 'nowrap',
                                            border: isDarkMode ? '1px solid rgba(24, 144, 255, 0.4)' : undefined
                                        }}
                                    >
                                        <Progress
                                            type="circle"
                                            percent={Math.round(activeImport.progress || 0)}
                                            size={14}
                                            strokeWidth={12}
                                            showInfo={false}
                                            status="active"
                                        />
                                        {Math.round(activeImport.progress || 0)}%
                                    </Button>
                                </Tooltip>
                            ) : (
                                <Tooltip title={t('start_historical_import')}>
                                    <Button
                                        type="default"
                                        size="small"
                                        icon={<HistoryOutlined />}
                                        onClick={handleOpenImportModal}
                                        style={{
                                            borderRadius: 100,
                                            fontSize: 12,
                                            fontWeight: 500,
                                            height: 28,
                                            padding: '0 12px',
                                            whiteSpace: 'nowrap',
                                            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : '#ffffff',
                                            border: `1px solid ${isDarkMode ? 'transparent' : '#d9d9d9'}`,
                                        }}
                                    >
                                        {t('sync')}
                                    </Button>
                                </Tooltip>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Search + Shared Filters + Reset */}
            <Flex
                wrap
                align="center"
                justify="space-between"
                style={{ marginTop: 10, gap: 8 }}
            >
                <Input.Search
                    placeholder={t('search_by_contact_name')}
                    style={{
                        flex: 1,
                        minWidth: 160,
                        backgroundColor: isDarkMode ? '#141414' : '#fff',
                        borderColor: themeColors.border,
                        color: themeColors.text
                    }}
                    onChange={onSearch}
                    loading={loading}
                    allowClear
                />

                <div className="conversation-filter-scroll">
                    <SharedFilterBar
                        fields={filterFields}
                        value={filters}
                        onChange={onFiltersChange}
                        sortFields={[
                            { value: 'updated_time', labelKey: 'filters_field_last_activity' },
                            { value: 'created_at', labelKey: 'created_at' },
                            { value: 'updated_at', labelKey: 'updated_at' },
                            { value: 'status', labelKey: 'filters_field_status' },
                            { value: 'contact_name', labelKey: 'full_name' },
                        ]}
                        sortValue={sortValue}
                        onSortChange={onSortChange}
                    />
                </div>
            </Flex>

            {/* Historical Import Modal */}
            <HistoricalImportModal
                open={isImportModalOpen}
                channelId={selectedChannel}
                onCancel={handleCloseImportModal}
                onSuccess={handleImportSuccess}
            />
        </Header>
    );
};

export default ConversationsHeader;

